import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { FunctionComponent, useEffect } from 'react';
import { runInBackground, runInBackgroundCallback } from '../../../../../configuration/setup/backgroundActions';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import {
    usePutAcknowledgeServiceOrderChangesMutation,
    usePutArchiveServiceOrderMutation,
} from '../../../api/serviceOrdersApi';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { showSuccessNotification } from '../../../notifications/SuccessNotification';
import { getSelectedServiceOrder, serviceOrdersSlice } from '../../../reducers/serviceOrdersSlice';
import { ServiceOrder } from '../serviceOrder.types';
import { getServiceNameByType } from '../serviceOrderUtils';
import { ServiceOrderSidebarBody } from './ServiceOrderSidebarBody';
import { ServiceOrderOrOptionSidebarHeader } from '../../shared/sidebar/ServiceOrderOrOptionSidebarHeader';
import { ServiceOrderSidebarFooter } from './ServiceOrderSidebarFooter';
import { useServiceOrderExecutionForm } from './useServiceOrderExecutionForm';

export const ServiceOrderSidebar: FunctionComponent = () => {
    const selectedServiceOrder = useAppSelector(getSelectedServiceOrder);
    return <ServiceOrderSidebarInternal selectedServiceOrder={selectedServiceOrder} key={selectedServiceOrder?.id} />;
};

const ServiceOrderSidebarInternal = ({ selectedServiceOrder }: { selectedServiceOrder: ServiceOrder | undefined }) => {
    const dispatch = useAppDispatch();
    const [acknowledgeChanges] = usePutAcknowledgeServiceOrderChangesMutation();
    const [archiveServiceOrder, archiveRequest] = usePutArchiveServiceOrderMutation();

    const closeSidebar = () => {
        dispatch(serviceOrdersSlice.actions.deselectServiceOrder());
    };

    const { formErrors, executionDetails, setExecutionDetails, handleSaveServiceOrder, isExecutionDetailsLoading } =
        useServiceOrderExecutionForm(selectedServiceOrder, closeSidebar);

    useEffect(() => {
        return () => {
            if (selectedServiceOrder !== undefined && selectedServiceOrder.hasUnacknowledgedExternalChanges) {
                runInBackground(
                    acknowledgeChanges({
                        serviceOrderId: selectedServiceOrder.id,
                    }),
                );
            }
        };
    }, [acknowledgeChanges, selectedServiceOrder]);

    const sidebarProperties = {
        showHeaderBorder: true,
        resizable: true,
        position: Sidebar.RIGHT,
        minWidth: 400,
        maxWidth: 550,
        width: 550,
        fly: true,
    };

    if (selectedServiceOrder === undefined) {
        return <Sidebar {...sidebarProperties} closed />;
    }

    const handleArchiveServiceOrder = runInBackgroundCallback(async () => {
        await archiveServiceOrder({ serviceOrderId: selectedServiceOrder.id })
            .unwrap()
            .then(() => {
                showSuccessNotification('outboundPortal.notification.archived.success');
                closeSidebar();
            })
            .catch(handleQueryError);
    });

    const title = (
        <div className="text-size-16">
            <span>{selectedServiceOrder.type} - </span>
            {getServiceNameByType(selectedServiceOrder.type)}
        </div>
    );

    return (
        <Sidebar
            {...sidebarProperties}
            title={title}
            titleClassName="padding-left-10"
            closed={false}
            onClose={closeSidebar}
            footer={
                <ServiceOrderSidebarFooter
                    serviceOrder={selectedServiceOrder}
                    handleArchiveServiceOrder={handleArchiveServiceOrder}
                    handleSaveServiceOrder={handleSaveServiceOrder}
                    onClose={closeSidebar}
                    isRequestPending={archiveRequest.isLoading || isExecutionDetailsLoading}
                />
            }
            footerClassName="margin-right-10"
        >
            <ServiceOrderOrOptionSidebarHeader serviceOrderOrOption={selectedServiceOrder} />
            <ServiceOrderSidebarBody
                errors={formErrors}
                serviceOrder={selectedServiceOrder}
                setExecutionDetailsFormData={setExecutionDetails}
                executionDetailsFormData={executionDetails}
            />
        </Sidebar>
    );
};
